import {
  Button,
  Box,
  StackSeparator,
  Text,
  Stack,
  Spacer,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { AttributeItem } from "./AttributeItem";
import { useEffect, useState } from "react";
import {
  CraAttribute,
  CraAttributes,
  CraAttributeName,
} from "@keita/lanepatrol-sdk";
import { AttributeSorting } from "../../../../../../map/models/MapFilters";
import { getAttributeValue } from "../../../../../../coding/utils/attribute-value";
import { CraAttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import { LoadingScreen } from "../../../../../../shared/views/LoadingScreen/LoadingScreen";
import { ScoreImpact } from "../../../../../../coding/models/ScoreImpact";
import { useStores } from "../../../../../../shared/stores/RootStore";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
} from "../../../../../../shared/views/icons";
import { CustomMenu } from "../../../../../../shared/views/CustomMenu";

export interface AttributeData {
  label: string;
  scoreImpact: number;
  attribute: CraAttribute;
  value: any;
  valueIsForecasted: boolean;
}

const sortingOptions = [
  {
    icon: <Icon as={ArrowDownIcon} />,
    key: AttributeSorting.Alphabetic,
  },
  {
    icon: <Icon as={ArrowUpIcon} />,
    key: AttributeSorting.ScoreImpact,
  },
];

const AttributeListBase: React.FC = () => {
  const { filterStore, craWayStore, craNodeStore, codingStore, craForecastStore } =
    useStores();
  const [contributions, setContributions] = useState<AttributeData[]>();
  const way = craWayStore.getSelected()!;
  const node = craNodeStore.getSelected()!;
  const allNodeCodingsByWayId =
    filterStore.originalOrForecastedAllNodeCodingsByWayId;
  const codingEntry = allNodeCodingsByWayId.get(way.id!)?.get(node.id!);

  const sortContributions = (list: AttributeData[]) => {
    switch (filterStore.attributeSorting) {
      case AttributeSorting.Alphabetic:
        return [...list].sort((a, b) => a.label.localeCompare(b.label));
      case AttributeSorting.ScoreImpact:
        return [...list].sort((a, b) => b.scoreImpact - a.scoreImpact);
    }
  };

  useEffect(() => {
    if (contributions?.length) {
      setContributions(sortContributions(contributions));
    }
    // eslint-disable-next-line
  }, [filterStore.attributeSorting]);

  useEffect(() => {
    if (!codingEntry) return;

    const originalCraNodeCoding = codingStore.getFromSelectedNode()!;

    const data: AttributeData[] = [];
    const flattenedScoreImpact = node.scoreImpact
      ? flattenScoreImpact(node.scoreImpact)
      : buildMockAttributes();

    flattenedScoreImpact.forEach((percentage, attributeName) => {
      const value = getAttributeValue(attributeName, codingEntry.attributes);
      var attribute = CraAttributes.getByName(attributeName)!;
      data.push({
        label: CraAttributeUtils.getLabel(attribute.name),
        scoreImpact: percentage,
        value,
        attribute: attribute,
        valueIsForecasted:
          !!craForecastStore.computedValues &&
          value !==
            getAttributeValue(attributeName, originalCraNodeCoding.attributes),
      });
    });

    setContributions(sortContributions(data));
    // eslint-disable-next-line
  }, [codingEntry, node, craForecastStore.computedValues]);

  if (!codingEntry) return null;

  return (
    <Box position="relative">
      {contributions ? (
        <>
          <HStack mb={4}>
            <Spacer />
            {node.scoreImpact ? (
              <CustomMenu
                items={sortingOptions.map(({ icon, key }) => ({
                  icon: icon,
                  onClick: () => filterStore.setAttributeSorting(key),
                  text: t(`map:attributes.sorting.${key}`),
                  key: key,
                }))}
              >
                <Button
                  as={Button}
                  variant="ghost"
                  size="sm"
                  fontSize={"sm"}
                  colorPalette="primary"
                  height={10}
                >
                  <Stack gap={0} textAlign="right">
                    <HStack>
                      <Icon as={ChevronDownIcon} />
                      <Box>{t("map:attributes.sortedBy")}</Box>
                    </HStack>
                    <Box color="gray.600" fontWeight="normal">
                      {t(
                        `map:attributes.sorting.${filterStore.attributeSorting}`,
                      )}
                    </Box>
                  </Stack>
                </Button>
              </CustomMenu>
            ) : (
              <Text textAlign="right" pl={2} fontSize="xs">
                {t("map:attributes.noImpact")}
              </Text>
            )}
          </HStack>
          <Stack separator={<StackSeparator />} gap="4">
            {contributions.map((entry, i) => (
              <AttributeItem {...entry} key={i} />
            ))}
          </Stack>
        </>
      ) : (
        <LoadingScreen />
      )}
    </Box>
  );
};

export const AttributeList = observer(AttributeListBase);

const flattenScoreImpact = (scoreImpact: ScoreImpact) => {
  const flattenedScoreImpact = new Map<CraAttributeName, number>();
  for (const [key, maybePercentage] of Object.entries(scoreImpact)) {
    if (typeof maybePercentage === "number") {
      flattenedScoreImpact.set(key as CraAttributeName, maybePercentage);
    } else {
      for (const [subKey, percentage] of Object.entries(maybePercentage)) {
        flattenedScoreImpact.set(
          `${key}.${subKey}` as CraAttributeName,
          percentage as number,
        );
      }
    }
  }
  return flattenedScoreImpact;
};

const buildMockAttributes = () => {
  return new Map<CraAttributeName, number>(
    CraAttributes.all.map((attr) => [attr.name, 0]),
  );
};
